import React, { useState, useEffect, useRef } from 'react';

const DRAG_THRESHOLD = 4;
const RETURN_AFTER_MS = 6000;
const NOT_A_HANDLE = 'button, a, input, select, textarea, label, [contenteditable="true"], [role="slider"]';

/**
 * Shared fixed container for all toasts. Ensures a single stack so toasts
 * never overlap or float far from the edge. SaveCancelToast always anchors
 * at the bottom.
 *
 * The stack sits above an open modal's footer (--toast-lift, set by
 * OverlayModal). It can be dragged aside by any part that is not a control,
 * and slides back after a while unless the pointer is over it.
 *
 * Structure:
 * - toast-stack-main: Sync, Upload, Notification, EditorLock, Spread, etc.
 * - toast-stack-save-cancel: SaveCancelToast (always last = bottom)
 */
export default function ToastStack() {
    const [mounted, setMounted] = useState(false);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const stackRef = useRef(null);
    const dragRef = useRef(null);
    const returnTimer = useRef(null);
    const hoverRef = useRef(false);
    const movedRef = useRef(false);

    useEffect(() => {
        setMounted(true);
        return () => clearTimeout(returnTimer.current);
    }, []);

    const scheduleReturn = () => {
        clearTimeout(returnTimer.current);
        returnTimer.current = setTimeout(() => {
            if (hoverRef.current || dragRef.current) return scheduleReturn();
            setOffset({ x: 0, y: 0 });
        }, RETURN_AFTER_MS);
    };

    // Toasts are portalled in from elsewhere in the React tree, so their
    // events only reach the stack as DOM events, not through React props.
    const onPointerDown = (event) => {
        if (event.button !== 0 || event.target.closest(NOT_A_HANDLE)) return;
        if (!event.target.closest('.pointer-events-auto, .toast, .toast-card')) return;
        const rect = stackRef.current.getBoundingClientRect();
        dragRef.current = {
            id: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            from: offset,
            // How far the stack can go and stay on screen.
            minX: offset.x - rect.left,
            maxX: offset.x + (window.innerWidth - rect.right),
            minY: offset.y - rect.top,
            maxY: offset.y + (window.innerHeight - rect.bottom),
            active: false,
        };
        movedRef.current = false;
    };

    useEffect(() => {
        if (!mounted) return undefined;
        const onMove = (event) => {
            const drag = dragRef.current;
            if (!drag || event.pointerId !== drag.id) return;
            const dx = event.clientX - drag.startX;
            const dy = event.clientY - drag.startY;
            if (!drag.active) {
                if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
                drag.active = true;
                movedRef.current = true;
                clearTimeout(returnTimer.current);
                setDragging(true);
            }
            event.preventDefault();
            setOffset({
                x: Math.min(drag.maxX, Math.max(drag.minX, drag.from.x + dx)),
                y: Math.min(drag.maxY, Math.max(drag.minY, drag.from.y + dy)),
            });
        };
        const onUp = (event) => {
            const drag = dragRef.current;
            if (!drag || event.pointerId !== drag.id) return;
            dragRef.current = null;
            if (!drag.active) return;
            // The click that ends a drag fires before this timeout.
            setTimeout(() => { movedRef.current = false; }, 0);
            setDragging(false);
            scheduleReturn();
        };
        const stack = stackRef.current;
        const onEnter = () => { hoverRef.current = true; };
        const onLeave = () => { hoverRef.current = false; };
        const onClick = (event) => {
            if (!movedRef.current) return;
            movedRef.current = false;
            event.preventDefault();
            event.stopPropagation();
        };
        stack?.addEventListener('pointerdown', onPointerDown);
        stack?.addEventListener('pointerenter', onEnter);
        stack?.addEventListener('pointerleave', onLeave);
        stack?.addEventListener('click', onClick, true);
        window.addEventListener('pointermove', onMove, { passive: false });
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        return () => {
            stack?.removeEventListener('pointerdown', onPointerDown);
            stack?.removeEventListener('pointerenter', onEnter);
            stack?.removeEventListener('pointerleave', onLeave);
            stack?.removeEventListener('click', onClick, true);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
        };
    });

    if (!mounted || typeof document === 'undefined') {
        return null;
    }

    const moved = offset.x !== 0 || offset.y !== 0;

    return (
        <div
            ref={stackRef}
            id="toast-stack"
            className={`fixed bottom-2 left-2 right-2 sm:bottom-4 sm:right-4 sm:left-auto sm:max-w-[28rem] sm:w-full max-w-full z-[99999] flex flex-col gap-2 items-end pointer-events-none min-w-0${dragging ? ' select-none' : ''}`}
            style={{
                bottom: 'calc(var(--toast-lift, 0px) + 0.75rem)',
                transform: moved ? `translate(${offset.x}px, ${offset.y}px)` : undefined,
                transition: dragging ? 'none' : 'transform 300ms ease, bottom 200ms ease',
                cursor: dragging ? 'grabbing' : undefined,
                touchAction: dragging ? 'none' : undefined,
            }}
            role="region"
            aria-label="Toasts"
        >
            <div
                id="toast-stack-main"
                className="flex flex-col gap-2 items-end w-full min-w-0"
            />
            <div
                id="toast-stack-save-cancel"
                className="flex flex-col gap-2 items-end w-full min-w-0"
            />
        </div>
    );
}
